import axios from 'axios';
import { API_BASE_URL } from '../config/config';
import CryptoJS from 'crypto-js';
import debugService from './debugService';
import { handleSessionExpired } from '../utils/sessionExpiredHandler';

// Encryption key and salt (should match backend)
const ENCRYPTION_KEY = 'C0c@C0l@P0rt@lK3y2024!@#$%^&*()';
const ENCRYPTION_SALT = 'CDEGr33nT@gS@lt!';

interface DynamicEntityRequest {
  entityName: string;
  greenTagEncrypted: string;
  operationType?: 'GET' | 'INSERT' | 'UPDATE' | 'DELETE';
  data?: any;
  additionalParams?: Record<string, any>;
}

interface DynamicEntityResponse {
  success: boolean;
  message: string;
  data?: any;
  entityName?: string;
  timestamp?: string;
  metadata?: Record<string, any>;
}

class DynamicEntityService {
  private apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 120000, // Increased to 2 minutes (120 seconds)
    headers: {
      'Content-Type': 'application/json',
    },
  });

  constructor() {
    // Add request interceptor for auth token
    this.apiClient.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor for error handling
    this.apiClient.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          handleSessionExpired();
        }
        return Promise.reject(error);
      }
    );

    // Add debug interceptors
    debugService.addInterceptors(this.apiClient, 'DynamicEntity');
  }

  /**
   * Encrypt a green tag using AES encryption
   */
  encryptGreenTag(greenTag: string): string {
    try {
      // Create a key from the encryption key and salt
      const key = CryptoJS.PBKDF2(ENCRYPTION_KEY, ENCRYPTION_SALT, {
        keySize: 256 / 32,
        iterations: 10000,
      });

      // Generate IV
      const iv = CryptoJS.lib.WordArray.random(128 / 8);

      // Encrypt
      const encrypted = CryptoJS.AES.encrypt(greenTag, key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      });

      // Combine IV and encrypted text
      const combined = iv.concat(encrypted.ciphertext);
      return CryptoJS.enc.Base64.stringify(combined);
    } catch (error) {
      console.error('Encryption error:', error);
      throw new Error('Failed to encrypt green tag');
    }
  }

  /**
   * Alternative: Use server-side encryption endpoint
   */
  async encryptGreenTagServer(greenTag: string): Promise<string> {
    try {
      const response = await this.apiClient.post('/dynamicentity/encrypt', {
        plainText: greenTag,
      });
      return response.data.encrypted;
    } catch (error) {
      console.error('Server encryption error:', error);
      throw new Error('Failed to encrypt green tag on server');
    }
  }

  /**
   * Get form configuration for an entity
   */
  async getFormConfiguration(entityName: string, tableType: string = 'header'): Promise<DynamicEntityResponse> {
    try {
      const request = {
        entityName,
        tableType
      };

      const response = await this.apiClient.post('/dynamicentity/getFormConfig', request);
      return response.data;
    } catch (error: any) {
      console.error('Get form config error:', error);
      console.error('Error response:', error.response?.data);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get form configuration',
      };
    }
  }

  /**
   * Get entity data
   */
  async getEntityData(entityName: string, greenTag: string, additionalParams?: any): Promise<DynamicEntityResponse> {
    try {
      const encryptedGreenTag = await this.encryptGreenTagServer(greenTag);

      // Get customer code from localStorage
      const customerData = localStorage.getItem('customerData');
      let customerCode = '';
      
      if (customerData) {
        try {
          const customer = JSON.parse(customerData);
          customerCode = customer.code || customer.Code ||
                        customer.number || customer.Number ||
                        customer.customerNumber || customer.CustomerNumber || '';
        } catch (e) {
          console.error('Error parsing customer data:', e);
        }
      }

      // Merge customer code with additional params
      const requestData = {
        ...(additionalParams || {}),
        customerCode: customerCode
      };

      const request: DynamicEntityRequest = {
        entityName,
        greenTagEncrypted: encryptedGreenTag,
        operationType: 'GET',
        data: requestData,  // Pass the params with customer code
        additionalParams: requestData,
      };

      const response = await this.apiClient.post('/dynamicentity/get', request);
      return response.data;
    } catch (error: any) {
      console.error('Get entity data error:', error);
      console.error('Error response:', error.response?.data);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get entity data',
      };
    }
  }

  /**
   * Insert entity data
   */
  async insertEntityData(entityName: string, greenTag: string, data: any): Promise<DynamicEntityResponse> {
    try {
      const encryptedGreenTag = await this.encryptGreenTagServer(greenTag);
      
      // Get customer code from localStorage
      const customerData = localStorage.getItem('customerData');
      let customerCode = '';
      
      if (customerData) {
        try {
          const customer = JSON.parse(customerData);
          customerCode = customer.code || customer.Code ||
                        customer.number || customer.Number ||
                        customer.customerNumber || customer.CustomerNumber || '';
        } catch (e) {
          console.error('Error parsing customer data:', e);
        }
      }

      // Merge customer code with data
      const requestData = {
        ...data,
        customerCode: customerCode
      };
      
      const request: DynamicEntityRequest = {
        entityName,
        greenTagEncrypted: encryptedGreenTag,
        operationType: 'INSERT',
        data: requestData,
        additionalParams: {},
      };

      const response = await this.apiClient.post('/dynamicentity/insert', request);
      return response.data;
    } catch (error: any) {
      console.error('Insert entity data error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to insert entity data',
      };
    }
  }

  /**
   * Update entity data
   */
  async updateEntityData(entityName: string, greenTag: string, data: any): Promise<DynamicEntityResponse> {
    try {
      const encryptedGreenTag = await this.encryptGreenTagServer(greenTag);
      
      // Get customer code from localStorage
      const customerData = localStorage.getItem('customerData');
      let customerCode = '';
      
      if (customerData) {
        try {
          const customer = JSON.parse(customerData);
          customerCode = customer.code || customer.Code ||
                        customer.number || customer.Number ||
                        customer.customerNumber || customer.CustomerNumber || '';
        } catch (e) {
          console.error('Error parsing customer data:', e);
        }
      }

      // Merge customer code with data
      const requestData = {
        ...data,
        customerCode: customerCode
      };
      
      const request: DynamicEntityRequest = {
        entityName,
        greenTagEncrypted: encryptedGreenTag,
        operationType: 'UPDATE',
        data: requestData,
      };

      const response = await this.apiClient.post('/dynamicentity/update', request);
      return response.data;
    } catch (error: any) {
      console.error('Update entity data error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update entity data',
      };
    }
  }

  /**
   * Validate green tag
   */
  async validateGreenTag(greenTag: string, entityName?: string): Promise<any> {
    try {
      const encryptedGreenTag = await this.encryptGreenTagServer(greenTag);
      
      const response = await this.apiClient.post('/dynamicentity/validate', {
        greenTagEncrypted: encryptedGreenTag,
        entityName,
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Validate green tag error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to validate green tag',
      };
    }
  }

  /**
   * Get entity configuration
   */
  async getEntityConfig(entityName: string): Promise<any> {
    try {
      const response = await this.apiClient.get(`/dynamicentity/config/${entityName}`);
      return response.data;
    } catch (error: any) {
      console.error('Get entity config error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get entity configuration',
      };
    }
  }

  /**
   * Submit service request using dynamic entity
   */
  async submitServiceRequest(greenTag: string, requestData: any): Promise<DynamicEntityResponse> {
    return this.insertEntityData('ServiceRequest', greenTag, requestData);
  }

  /**
   * Submit complaint using dynamic entity
   */
  async submitComplaint(greenTag: string, complaintData: any): Promise<DynamicEntityResponse> {
    return this.insertEntityData('Complaint', greenTag, complaintData);
  }

  /**
   * Submit registration using dynamic entity
   */
  async submitRegistration(greenTag: string, registrationData: any): Promise<DynamicEntityResponse> {
    return this.insertEntityData('Registration', greenTag, registrationData);
  }

  /**
   * Get ticket history using dynamic entity
   */
  async getTicketHistory(greenTag: string): Promise<DynamicEntityResponse> {
    return this.getEntityData('TicketHistory', greenTag);
  }

  /**
   * Get training modules using dynamic entity
   */
  async getTrainingModules(greenTag: string): Promise<DynamicEntityResponse> {
    return this.getEntityData('Training', greenTag);
  }

  /**
   * Get product information using dynamic entity
   */
  async getProductInformation(greenTag: string, productParams?: any): Promise<DynamicEntityResponse> {
    return this.getEntityData('ProductInfo', greenTag, productParams);
  }
}

// Export singleton instance
const dynamicEntityService = new DynamicEntityService();
export default dynamicEntityService;

// Export class for testing
export { DynamicEntityService };